// Usage stats for a doc (CONTRACTS §4). Opened from the viewer's More menu.
//
// Two tiers, matching firestore.rules:
//   - AGGREGATE (DocMeta.stats) — any member who can read the doc. Already on
//     the loaded meta, so it renders with zero extra queries.
//   - PER-PERSON (viewers/ + views/) — owner + editors only (canEdit). Fetched
//     lazily, and only when `canSeeDetail` is true; asking otherwise would just
//     earn a permission-denied.
//
// Comment counts are derived from the annotation list the viewer already
// subscribes to, so they cost nothing either.

import { useEffect, useState } from "react";
import { Modal } from "./Modal";
import {
  formatDuration,
  formatRelative,
  formatTimestamp,
  listDocViewers,
  listViewSessions,
} from "../firestore";
import type { Annotation, DocMeta, ViewerStat, ViewSession } from "../types";

interface Props {
  meta: DocMeta;
  versionCount: number;
  annotations: Annotation[];
  /** True for owner + editors — mirrors firestore.rules canEdit(). */
  canSeeDetail: boolean;
  onClose: () => void;
}

export function StatsPanel({
  meta,
  versionCount,
  annotations,
  canSeeDetail,
  onClose,
}: Props) {
  const [viewers, setViewers] = useState<ViewerStat[] | null>(null);
  const [sessions, setSessions] = useState<ViewSession[] | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    if (!canSeeDetail) return;
    let alive = true;
    Promise.all([listDocViewers(meta.docId), listViewSessions(meta.docId)])
      .then(([v, s]) => {
        if (!alive) return;
        setViewers(v);
        setSessions(s);
      })
      .catch(() => {
        if (alive) setDetailError("Could not load the per-person breakdown.");
      });
    return () => {
      alive = false;
    };
  }, [meta.docId, canSeeDetail]);

  const stats = meta.stats;
  const views = stats?.views ?? 0;
  const uniqueViewers = stats?.viewers ?? 0;
  const totalMs = stats?.totalMs ?? 0;
  const open = annotations.filter((a) => !a.resolved).length;
  const resolved = annotations.length - open;

  return (
    <Modal title={`Stats — ${meta.title}`} onClose={onClose} wide>
      <div className="stat-grid">
        <Stat label="Views" value={String(views)} />
        <Stat label="Unique viewers" value={String(uniqueViewers)} />
        <Stat label="Total time" value={formatDuration(totalMs)} />
        <Stat
          label="Avg per view"
          value={views > 0 ? formatDuration(Math.round(totalMs / views)) : "—"}
        />
      </div>

      <dl className="stat-facts">
        <div>
          <dt>Last viewed</dt>
          <dd>
            {stats?.lastViewedAt ? formatRelative(stats.lastViewedAt) : "never"}
            {stats?.lastViewedByEmail ? ` · ${stats.lastViewedByEmail}` : ""}
          </dd>
        </div>
        <div>
          <dt>Versions published</dt>
          <dd>{versionCount}</dd>
        </div>
        <div>
          <dt>Last edited</dt>
          <dd>
            {stats?.lastEditedAt ? formatRelative(stats.lastEditedAt) : "never"}
            {stats?.lastEditedByEmail ? ` · ${stats.lastEditedByEmail}` : ""}
          </dd>
        </div>
        <div>
          <dt>Comments on this version</dt>
          <dd>
            {open} open · {resolved} resolved
          </dd>
        </div>
      </dl>

      {!canSeeDetail && (
        <p className="caption stat-note">
          Who opened this document, and for how long, is visible to the owner and
          editors only.
        </p>
      )}

      {canSeeDetail && (
        <>
          <h3 className="stat-section">Who has read it</h3>
          {detailError && (
            <p className="caption stat-note" role="alert">
              {detailError}
            </p>
          )}
          {!detailError && viewers === null && (
            <p className="caption stat-note">Loading…</p>
          )}
          {viewers?.length === 0 && (
            <p className="caption stat-note">Nobody has opened this yet.</p>
          )}
          {!!viewers?.length && (
            <div className="stat-table-wrap">
              <table className="stat-table">
                <thead>
                  <tr>
                    <th scope="col">Person</th>
                    <th scope="col">Views</th>
                    <th scope="col">Time</th>
                    <th scope="col">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {viewers.map((v) => (
                    <tr key={v.uid}>
                      <td>{v.email}</td>
                      <td>{v.views ?? 0}</td>
                      <td>{formatDuration(v.totalMs)}</td>
                      <td>{formatRelative(v.lastViewedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="stat-section">Recent activity</h3>
          {sessions?.length === 0 && (
            <p className="caption stat-note">No sessions recorded yet.</p>
          )}
          {!!sessions?.length && (
            <ul className="stat-timeline">
              {sessions.map((s) => (
                <li key={s.sessionId}>
                  <span className="stat-timeline-who">{s.email}</span>
                  <span className="caption">
                    {s.versionId} · {formatDuration(s.ms)} ·{" "}
                    <time>{formatTimestamp(s.startedAt)}</time>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Modal>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat-tile">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}
